import { useMemo, useState } from 'react';
import { Pagination } from 'antd';
import { Tooltip, Tag } from '@bancobarigui/barihaus';
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { PendingItem } from '../types';

const DEFAULT_PAGE_SIZE = 5;

interface PendingItemsListProps {
  items: PendingItem[];
  onItemClick?: (itemId: string) => void;
}

const PRIORITY_CONFIG: Record<string, { color: 'red' | 'orange' | 'blue'; label: string }> = {
  high: { color: 'red', label: 'Alta' },
  medium: { color: 'orange', label: 'Média' },
  low: { color: 'blue', label: 'Baixa' },
};

function getOverdueText(daysOverdue: number): string {
  if (daysOverdue === 0) return 'Hoje';
  if (daysOverdue === 1) return '1 dia';
  return `${daysOverdue} dias`;
}

function formatPendingType(type: string): string {
  if (!type) return '';

  const typeLabels: Record<string, string> = {
    'ContatoNaoResponde': 'Contato Não Responde',
    'DocumentacaoIncompleta': 'Documentação Incompleta',
    'AgendamentoNaoConfirmado': 'Agendamento Não Confirmado',
    'LaudoIncompleto': 'Laudo Incompleto',
    'RevisaoNecessaria': 'Revisão Necessária',
    'OutraPendencia': 'Outra Pendência',
    'AguardandoDocumentos': 'Aguardando Documentos',
    'PendenciaFinanceira': 'Pendência Financeira',
  };

  if (typeLabels[type]) {
    return typeLabels[type];
  }

  return type
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

export function PendingItemsList({ items, onItemClick }: PendingItemsListProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = DEFAULT_PAGE_SIZE;

  const pagedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return items.slice(start, end);
  }, [items, currentPage, pageSize]);

  if (items.length === 0) {
    return (
      <div className="bg-green-50 rounded-lg border border-green-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <CheckCircleOutlined className="text-green-600" />
          <span className="text-sm text-green-700">Tudo em dia! Não há pendências no momento.</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
        {pagedItems.map((item) => {
          const priorityCfg = PRIORITY_CONFIG[item.priority];
          const overdue = item.daysOverdue > 0;
          return (
            <button
              key={item.id}
              onClick={() => onItemClick?.(item.inspectionItemId)}
              className="group text-left w-full"
            >
              <div className="rounded-lg bg-white px-4 py-3 shadow-sm hover:shadow-md transition-shadow duration-200" style={{ border: '1px solid #d9d9d9' }}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center h-9 min-w-9 px-2 rounded-md bg-blue-50 text-blue-700 font-semibold shrink-0" style={{ fontSize: 14 }}>
                      {String(item.code)}
                    </span>
                    <div className="space-y-0.5">
                      <div className="font-semibold text-gray-800 leading-tight" style={{ fontSize: 16 }}>{formatPendingType(item.type)}</div>
                      <div className="text-gray-500" style={{ fontSize: 12 }}>Pendência</div>
                    </div>
                  </div>
                  <Tag color={priorityCfg.color} className="!m-0 !py-[2px] !px-2 !rounded-full" style={{ fontSize: 12 }}>
                    {priorityCfg.label}
                  </Tag>
                </div>

                <div className="mt-2">
                  <Tooltip title={item.description}>
                    <p className="text-gray-600 leading-snug line-clamp-2" style={{ fontSize: 14 }}>{item.description}</p>
                  </Tooltip>
                </div>

                <div className="mt-3 flex items-center justify-between">
                  <div className={`inline-flex items-center gap-1.5 rounded-full px-2 py-[6px] font-medium ${overdue ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-600'}`} style={{ fontSize: 12 }}>
                    {overdue && <ClockCircleOutlined />}
                    {getOverdueText(item.daysOverdue)}
                  </div>
                  <span className="text-gray-400 group-hover:text-gray-600 transition-colors" style={{ fontSize: 12 }}>Abrir detalhe</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex justify-end">
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={items.length}
          onChange={setCurrentPage}
          showSizeChanger={false}
          showTotal={(total, range) => `${range[0]}-${range[1]} de ${total}`}
        />
      </div>
    </div>
  );
}
